/**
 * react-query hooks for the data the protocol analysis route loads up front.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { toast } from "react-toastify";

import { getAnalyses } from "../../../../apis/analysisApi";
import {
	getProtocolDashboardOptions,
	getProtocolData,
	getProtocolTreatmentData,
	updateTrialsMetaTags,
} from "../../../../apis/protocolApi";
import { getMeanComparisonsOptions } from "../../../../apis/statisticsApi";
import type { AerialPlotResponse } from "../../../../apis/types";
import type { TrialMetaTagsUpdate, UpdateTrialsMetaTagsResponse } from "../../../../apis/types/protocolTypes";
import type { TrialMetadataInfo, TrialsMetadata } from "../../../../apis/types/trialTypes";
import { getGduDataForTrials } from "../../../../apis/weatherDataApi";
import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import { useProtocolAnalysis, useProtocolAnalysisDispatch } from "./ProtocolAnalysisContext";

/** Thrown when a response's `statusCode` isn't 200. Carries the response's `data`. */
export class ProtocolAnalysisApiError extends Error {
	constructor(readonly responseData: unknown) {
		super(typeof responseData === "string" ? responseData : "Request failed");
		this.name = "ProtocolAnalysisApiError";
	}
}

/** Returns `res.data`, throwing when `statusCode` isn't 200 (errors still come back as HTTP 200, with the message in `data`). */
const unwrap = <TData>(res: AerialPlotResponse<TData>): TData => {
	if (res.statusCode !== 200) {
		throw new ProtocolAnalysisApiError(res.data);
	}
	return res.data;
};

/**
 * The ids the queries here are keyed by. `clientReady` gates the client-wide queries,
 * `protocolReady` the per-protocol ones.
 */
const useQueryIds = () => {
	const userAuth = useUserAuth();
	const moduleNavigation = useModuleNavigation();
	const clientId = userAuth.currentClientId;
	const protocolId = moduleNavigation.protocolId;
	const clientReady = userAuth.isReady && !!clientId;
	return { clientId, protocolId, clientReady, protocolReady: clientReady && !!protocolId };
};

/**
 * GetProtocolData for the protocol in the url. Shared by index.tsx and the four pages (same key, so
 * one request). `data.protocolData` is null when the protocol isn't found for this client.
 *
 * index.tsx owns the fetch and only mounts the pages once it has the data, so the pages pass
 * `refetchOnMount: false` to read the cache without refetching on every page switch.
 */
export const useProtocolDataQuery = ({ refetchOnMount = true }: { refetchOnMount?: boolean } = {}) => {
	const { getTokenSilently } = useAuth0();
	const { clientId, protocolId, protocolReady } = useQueryIds();

	return useQuery({
		queryKey: ["protocol data", protocolId, clientId],
		queryFn: async () => unwrap(await getProtocolData(protocolId!, clientId!, await getTokenSilently())),
		enabled: protocolReady,
		refetchOnMount,
		retry: false,
	});
};

/** GetProtocolTreatmentData. Resolves `data` (`{ protocolTreatments, protocolTreatmentApplications }`). */
export const useProtocolTreatmentDataQuery = () => {
	const { getTokenSilently } = useAuth0();
	const { clientId, protocolId, protocolReady } = useQueryIds();

	return useQuery({
		queryKey: ["protocol treatment data", protocolId, clientId],
		queryFn: async () => unwrap(await getProtocolTreatmentData(protocolId!, clientId!, await getTokenSilently())),
		enabled: protocolReady,
		retry: false,
	});
};

/**
 * ProtocolDashboardOptions. The key starts `["protocol dashboard options", protocolId]` because
 * HeatmapExportModal invalidates that prefix.
 */
export const useProtocolDashboardOptionsQuery = () => {
	const { getTokenSilently } = useAuth0();
	const { clientId, protocolId, protocolReady } = useQueryIds();

	return useQuery({
		queryKey: ["protocol dashboard options", protocolId, clientId],
		queryFn: async () => unwrap(await getProtocolDashboardOptions(protocolId!, clientId!, await getTokenSilently())),
		enabled: protocolReady,
		retry: false,
	});
};

/**
 * GetAnalyses. Resolves the whole response; the pages read `.data`.
 */
export const useAnalysesQuery = () => {
	const { getTokenSilently } = useAuth0();
	const { clientId, clientReady } = useQueryIds();

	return useQuery({
		queryKey: ["analyses", clientId],
		queryFn: async () => {
			const res = await getAnalyses(clientId!, await getTokenSilently());
			if ("ok" in res && res.ok === false) {
				throw new ProtocolAnalysisApiError(res);
			}
			return res;
		},
		enabled: clientReady,
		retry: false,
	});
};

/**
 * GetMeanComparisonsOptions. Resolves the whole response.
 */
export const useMeanComparisonOptionsQuery = () => {
	const { getTokenSilently } = useAuth0();
	const { clientId, clientReady } = useQueryIds();

	return useQuery({
		queryKey: ["mean comparison options", clientId],
		queryFn: async () => {
			const res = await getMeanComparisonsOptions(clientId!, await getTokenSilently());
			if ("errors" in res && res.errors) {
				throw new ProtocolAnalysisApiError(res);
			}
			return res;
		},
		enabled: clientReady,
		retry: false,
	});
};

/** GetGduDataForTrials for the protocol's trials. Disabled until `trialIds` is set. */
export const useGduDataForTrialsQuery = (trialIds: string[] | undefined) => {
	const { getTokenSilently } = useAuth0();
	const { clientId, clientReady } = useQueryIds();

	return useQuery({
		queryKey: ["gdu data for trials", trialIds, clientId],
		queryFn: async () => unwrap(await getGduDataForTrials(trialIds!, clientId!, await getTokenSilently())),
		enabled: clientReady && !!trialIds,
		retry: false,
	});
};

/**
 * Saves the unsaved meta tag edits held by `ProtocolAnalysisContext`. Must be used inside
 * `ProtocolAnalysisProvider`.
 *
 * On success it writes the saved tags into the "trials metadata" cache (so every page sees them
 * without a refetch) and clears the edits. On failure the edits are kept so the user can retry.
 *
 * @param trialInfo The loaded trials' info, used to fill in the tags the user did not touch.
 * @returns `save` resolves true once saved, false if it failed (a toast has already been shown) or
 *   there was nothing to save.
 */
export const useSaveMetaTags = (trialInfo: TrialMetadataInfo[] | undefined) => {
	const { getTokenSilently } = useAuth0();
	const { clientId } = useQueryIds();
	const queryClient = useQueryClient();
	const { metaTagEdits } = useProtocolAnalysis();
	const { clearMetaTagEdits } = useProtocolAnalysisDispatch();

	const mutation = useMutation({
		mutationFn: async (updates: TrialMetaTagsUpdate[]) =>
			// protocolApi.js returns the parsed body untyped; nothing reads it
			updateTrialsMetaTags(updates, clientId!, await getTokenSilently()) as Promise<UpdateTrialsMetaTagsResponse>,
		onSuccess: async (_res, updates) => {
			const updatesByTrialId = _.keyBy(updates, "trialId");
			queryClient.setQueriesData<TrialsMetadata>({ queryKey: ["trials metadata"] }, (old) =>
				old?.map((trial) =>
					trial && updatesByTrialId[trial.trialInfo.trialId]
						? { ...trial, trialInfo: { ...trial.trialInfo, ...updatesByTrialId[trial.trialInfo.trialId] } }
						: trial,
				),
			);
			clearMetaTagEdits();
			toast.success("Meta Tags updated successfully!");
			await queryClient.invalidateQueries({ queryKey: ["meta factors", clientId] });
		},
		onError: (error) => {
			toast.error("Meta Tags failed to update.");
			console.error(error);
		},
	});

	const save = async (): Promise<boolean> => {
		const updates = _.compact(
			_.map(metaTagEdits, (edit, trialId): TrialMetaTagsUpdate | null => {
				const info = _.find(trialInfo, (ti) => ti.trialId === trialId);
				return info
					? {
							trialId,
							trialExclusionStatusId: info.trialExclusionStatusId,
							trialDataQualityId: info.trialDataQualityId,
							trialSiteResponseId: info.trialSiteResponseId,
							...edit,
						}
					: null;
			}),
		);
		if (updates.length === 0) return false;

		try {
			await mutation.mutateAsync(updates);
			return true;
		} catch {
			return false;
		}
	};

	return { save, isSaving: mutation.isPending };
};
